import type { EmblaOptionsType } from 'embla-carousel';
import AutoHeight from 'embla-carousel-auto-height';
import useEmblaCarousel from 'embla-carousel-react';
import * as React from 'react';

type Options = {
  slideCount: number;
  initialIndex?: number;
  reInitKey?: string;
  align?: EmblaOptionsType['align'];
  loop?: boolean;
  onSelect?: (index: number) => void;
  watchDrag?: EmblaOptionsType['watchDrag'];
  useAutoHeight?: boolean;
};

export const useEmblaAutoHeightCarousel = ({
  slideCount,
  initialIndex = 0,
  reInitKey,
  align = 'center',
  loop,
  onSelect,
  watchDrag,
  useAutoHeight = true,
}: Options) => {
  const hasMultipleSlides = slideCount > 1;
  const [selectedIndex, setSelectedIndex] = React.useState(initialIndex);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const emblaOptions = React.useMemo<EmblaOptionsType>(
    () => ({
      align,
      loop: typeof loop === 'boolean' ? loop : hasMultipleSlides,
      startIndex: initialIndex,
      watchDrag,
    }),
    [align, hasMultipleSlides, initialIndex, loop, watchDrag],
  );

  const autoHeightPlugin = React.useMemo(() => AutoHeight(), []);
  const plugins = React.useMemo(() => (useAutoHeight ? [autoHeightPlugin] : []), [autoHeightPlugin, useAutoHeight]);

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);

  React.useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();

      setSelectedIndex(index);
      onSelect?.(index);
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    emblaApi.on('select', handleSelect);
    emblaApi.on('reInit', handleSelect);
    handleSelect();

    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('reInit', handleSelect);
    };
  }, [emblaApi, onSelect]);

  React.useEffect(() => {
    if (!emblaApi) {
      return;
    }

    let frameId = 0;

    const requestReInit = () => {
      cancelAnimationFrame(frameId);
      const currentIndex = emblaApi.selectedScrollSnap();
      const reInitOptions = { ...emblaOptions, startIndex: currentIndex };

      frameId = requestAnimationFrame(() => emblaApi.reInit(reInitOptions, plugins));
    };

    requestReInit();

    const observer = new ResizeObserver(requestReInit);

    emblaApi.slideNodes().forEach((node) => observer.observe(node));

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [emblaApi, emblaOptions, plugins, reInitKey]);

  return {
    emblaApi,
    emblaRef,
    selectedIndex,
    canScrollPrev,
    canScrollNext,
    hasMultipleSlides,
  };
};
